import type { AgendaChangeEventDto, AgendaChangeVerb } from '@familyhub/shared';

/**
 * Clustering the flat change stream into cards — the same shape the groceries
 * activity feed uses (`web/src/activity/grouping.ts`): a run of events by the
 * **same person, same verb, same day**, close together in time, reads as one
 * sitting rather than one row per edit. A Tuesday evening pass that touches
 * five things should read as one card, not five near-identical rows.
 *
 * Grouping lives on the client because it is presentation, exactly like
 * `groupActivity`: the server decides *what* changed, the screen decides how it
 * reads. Keeping it a pure function is what lets the day-boundary and gap rules
 * be tested without a browser.
 */

/** A run of same-actor, same-verb changes shown as one card. */
export interface AgendaChangeCard {
  /** Stable React key: the newest event's own key, which is unique. */
  key: string;
  verb: AgendaChangeVerb;
  actor: { id: string | null; display_name: string };
  /** The newest event in the run — the date the card header shows. */
  at: string;
  events: AgendaChangeEventDto[];
}

/**
 * Events within this of each other join one card; a wider gap starts a new one.
 * Three hours is "one sitting" — the same window the groceries feed uses.
 */
const CLUSTER_GAP_MS = 3 * 60 * 60 * 1000;

/** Local calendar day, so "same day" means the household's midnight, not UTC's. */
function localDay(iso: string): string {
  return new Date(iso).toDateString();
}

export function groupAgendaChanges(events: AgendaChangeEventDto[]): AgendaChangeCard[] {
  const cards: AgendaChangeCard[] = [];

  for (const event of events) {
    const card = cards[cards.length - 1];
    const last = card?.events[card.events.length - 1];
    // `last` is the oldest event in the open card (events arrive newest-first),
    // so the gap to this still-older event is `last.at - event.at`.
    const joins =
      card !== undefined &&
      last !== undefined &&
      card.verb === event.verb &&
      card.actor.id === event.actor.id &&
      localDay(last.at) === localDay(event.at) &&
      Date.parse(last.at) - Date.parse(event.at) <= CLUSTER_GAP_MS;

    if (joins) {
      card!.events.push(event);
    } else {
      cards.push({
        key: `${event.entity}:${event.id}:${event.verb}:${event.at}`,
        verb: event.verb,
        actor: event.actor,
        at: event.at,
        events: [event],
      });
    }
  }

  return cards;
}
